import React from "react";

import { faCog } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FieldArrayWithId,
  FieldPathByValue,
  useFormContext,
} from "react-hook-form";

import { AiNodeJsonSchemaPropertyUI, AiNodeV2Form } from "src/aiNode/types";
import { Switch } from "src/base-components/Switch";
import { Icon } from "src/design-system/Icon";
import { useOpenOnHover } from "src/utils/useOpenOnHover";

type JsonSchemaRowAdvancedOptionsProps = {
  immutable: boolean;
  index: number;
  formLocation: string; // react-hook-forms path for binding the row data
  fieldData: FieldArrayWithId<
    AiNodeV2Form,
    FieldPathByValue<AiNodeV2Form, AiNodeJsonSchemaPropertyUI[] | undefined>
  >; // pass the field data directly to avoid useWatch sync issues
};

export const JsonSchemaRowAdvancedOptions: React.FC<
  JsonSchemaRowAdvancedOptionsProps
> = ({ immutable, formLocation, fieldData, index }) => {
  const hoverProps = useOpenOnHover<HTMLDivElement, HTMLDivElement>(50);

  const { control } = useFormContext();
  const field = fieldData as unknown as AiNodeJsonSchemaPropertyUI;
  return (
    <div className="relative">
      <div
        onMouseEnter={hoverProps.onMouseEnter}
        onMouseLeave={hoverProps.onMouseLeave}
      >
        <Icon
          variant={
            immutable ? "disabled" : hoverProps.isOpen ? "hover" : "default"
          }
          dataLoc={`schema-field-advanced-trigger-${field.type[1]}-${field.required}`}
          icon={faCog}
          size="xs"
        />
      </div>
      {hoverProps.isOpen && (
        <div
          className="absolute -right-3 z-50 w-[9.5rem] rounded-lg bg-white px-3 py-4 font-inter-normal-13px text-gray-800 shadow-lg"
          data-loc="schema-field-advanced-popup"
          onMouseEnter={hoverProps.onMouseEnter}
          onMouseLeave={hoverProps.onMouseLeave}
        >
          <div className="flex items-center justify-between">
            <span>Nullable</span>
            <Controller
              control={control}
              name={`${formLocation}.type.1` as const}
              render={(props) => (
                <Switch
                  dataLoc={`ai-node-field-nullable-${index}`}
                  disabled={immutable}
                  enabled={props.field.value === "null"}
                  onChange={() => {
                    props.field.onChange(
                      // Calling onChange with `undefined` is not valid in react-hook-form
                      // So use `false` instead. But it contradicts JSON schema.
                      props.field.value === "null" ? false : "null",
                    );
                  }}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};
